import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { recordTriggeredHint } from '../../../../actions/user.actions';
import { onboardingHints, routes } from '../../../../constants';
import { selectTriggeredHints, selectUser } from '../../../../reducers/user.selectors';
import { isWithinDaysOfSignup } from '../../../../utils/signupWindow';
import useRouter from '../../../../utils/useRouter';
import useShouldShowAIAssistant from '../../../../utils/useShouldShowAIAssistant';

export enum MukiBudeOnboardingStep {
	READER_HINT = 'reader-hint',
	IMAGE_GENERATOR_HINT = 'image-generator-hint',
}

export const MukiBudeOnboardingStepLocalStorageKey = {
	[MukiBudeOnboardingStep.READER_HINT]: onboardingHints.aiAssistant.readerHint,
	[MukiBudeOnboardingStep.IMAGE_GENERATOR_HINT]: onboardingHints.aiAssistant.imageGeneratorHint,
};

export type MukiBudeOnboardingStepConfig = {
	shouldShow: (context: {
		isReaderPage: boolean;
		isAIToolsPage: boolean;
		isHistoryPage: boolean;
		isSearchPage: boolean;
		isImageGeneratorEligible: boolean;
	}) => boolean;
};

export type MukiBudeOnboardingState = Record<
	MukiBudeOnboardingStep,
	{
		shouldShow: boolean;
	}
>;

const stepConfigs: Record<MukiBudeOnboardingStep, MukiBudeOnboardingStepConfig> = {
	[MukiBudeOnboardingStep.READER_HINT]: {
		shouldShow: ({ isReaderPage }) => isReaderPage,
	},
	[MukiBudeOnboardingStep.IMAGE_GENERATOR_HINT]: {
		shouldShow: ({
			isReaderPage,
			isAIToolsPage,
			isHistoryPage,
			isSearchPage,
			isImageGeneratorEligible,
		}) =>
			isImageGeneratorEligible && !isReaderPage && !isAIToolsPage && !isHistoryPage && !isSearchPage,
	},
};

const useMukiOnboarding = () => {
	const {
		match: {
			location: { pathname },
		},
	} = useRouter();
	const isReaderPage = pathname.includes(`/${routes.reader.root}/`);
	const isAIToolsPage = pathname.includes(`/${routes.aiTools.root}`);
	const isHistoryPage = pathname.includes(`/${routes.aiAssistantHistory.root}`);
	const isSearchPage = pathname.includes(`/${routes.search.root}`);
	const userHasAccess = useShouldShowAIAssistant();
	const user = useSelector(selectUser);
	// Step "seen" state now lives on the user object (server-side), keyed by the
	// same strings previously used in localStorage.
	const triggeredHints = useSelector(selectTriggeredHints);
	const dispatch = useDispatch();
	const isImageGeneratorEligible = !user?.isSchoolUser;
	// SEAR-704: withhold the image-generator hint from B2C mU Pro/mU 360 users in
	// their first 30 days so it doesn't overlap the new-user onboarding dialog.
	const isB2CProKombiUser =
		!!user &&
		!user.isSchoolUser &&
		!user.isLibraryUser &&
		!user.isKeycloakSSO &&
		(user.isBibliothekUser || user.isKombiUser);
	const suppressImageGeneratorHintForNewUser =
		isB2CProKombiUser && isWithinDaysOfSignup(user?.createdAt);

	const shouldShowStep = useCallback(
		(step: MukiBudeOnboardingStep) => {
			const shownBefore = triggeredHints.includes(MukiBudeOnboardingStepLocalStorageKey[step]);
			if (shownBefore || !userHasAccess || user?.isAiOnlyUser) return false;

			// SEAR-704: image-generator-hint only — gate on 30-day signup age for the B2C cohort.
			if (
				step === MukiBudeOnboardingStep.IMAGE_GENERATOR_HINT &&
				suppressImageGeneratorHintForNewUser
			) {
				return false;
			}

			const config = stepConfigs[step];

			return config.shouldShow({
				isReaderPage,
				isAIToolsPage,
				isHistoryPage,
				isSearchPage,
				isImageGeneratorEligible,
			});
		},
		[
			triggeredHints,
			userHasAccess,
			user?.isAiOnlyUser,
			suppressImageGeneratorHintForNewUser,
			isReaderPage,
			isAIToolsPage,
			isHistoryPage,
			isSearchPage,
			isImageGeneratorEligible,
		]
	);

	const [onboardingState, setOnboardingState] = useState<MukiBudeOnboardingState>(() => {
		const initialState: MukiBudeOnboardingState = {} as MukiBudeOnboardingState;

		for (const step of Object.values(MukiBudeOnboardingStep)) {
			initialState[step] = {
				shouldShow: shouldShowStep(step),
			};
		}

		return initialState;
	});

	const concludeOnboardingStep = (step: MukiBudeOnboardingStep) => {
		dispatch(recordTriggeredHint(MukiBudeOnboardingStepLocalStorageKey[step]));
		setOnboardingState((prev) => ({
			...prev,
			[step]: { ...prev[step], shouldShow: false },
		}));
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome hook deps kept as-is; changing the deps list alters effect timing
	useEffect(() => {
		const newState: MukiBudeOnboardingState = {} as MukiBudeOnboardingState;
		let hasChanges = false;

		for (const step of Object.values(MukiBudeOnboardingStep)) {
			const currentShouldShow = onboardingState[step]?.shouldShow ?? false;
			const newShouldShow = shouldShowStep(step);

			newState[step] = { shouldShow: newShouldShow };

			if (currentShouldShow !== newShouldShow) {
				hasChanges = true;
			}
		}

		if (hasChanges) {
			setOnboardingState(newState);
		}
	}, [shouldShowStep]);

	return {
		concludeOnboardingStep,
		onboardingState,
	};
};

export default useMukiOnboarding;
